import { useCallback, useState } from 'react'
import { CheckIcon, XIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { type Actor as ActorData, type ExtractedStorageResources } from '@campai/lib-common/entities'
import { Actor, ActorName, useUpload } from '@campai/lib-web/components'
import { RichTextEditor } from '@campai/lib-web/components/common/RichTextEditor'
import { Button, Card, CardHeader, Field, FieldLabel } from '@campai/lib-web/components/new/ui'

import { appState } from '@/appState'
import {
    AttachmentEditor,
    createAttachmentInput,
    createRichTextEditorResources,
    splitAttachments,
    type AttachmentInput,
} from '@/product/community/shared/Attachments'

type InlineContentComposerProps = {
    initialMessage?: string
    initialAttachments?: ExtractedStorageResources
    actor?: ActorData
    authorDescription?: string
    messageLabel: string
    submitLabel: string
    cancelLabel?: string
    onSubmit: (input: { message: string; attachments: AttachmentInput }) => Promise<void>
    onCancel?: () => void
}

export const InlineContentComposer = observer(
    ({
        initialMessage = '',
        initialAttachments = [],
        actor = {
            type: 'user',
            userId: appState.user.userId,
            name: appState.user.name,
            image: appState.user.avatar,
        },
        authorDescription,
        messageLabel,
        submitLabel,
        cancelLabel,
        onSubmit,
        onCancel,
    }: InlineContentComposerProps) => {
        const { t } = useTranslation()
        const initialSplitAttachments = splitAttachments(initialMessage, initialAttachments)
        const [message, setMessage] = useState(initialMessage)
        const [imageAttachments, setImageAttachments] = useState(initialSplitAttachments.imageAttachments)
        const [fileAttachments, setFileAttachments] = useState(initialSplitAttachments.fileAttachments)
        const [isSubmitting, setIsSubmitting] = useState(false)
        const [editorSessionKey, setEditorSessionKey] = useState(0)
        const { upload, isUploading } = useUpload()

        const resetDraft = useCallback(() => {
            const { imageAttachments, fileAttachments } = splitAttachments(initialMessage, initialAttachments)
            setMessage(initialMessage)
            setImageAttachments(imageAttachments)
            setFileAttachments(fileAttachments)
            setEditorSessionKey((currentKey) => currentKey + 1)
        }, [initialAttachments, initialMessage])

        const handleImageUpload = useCallback(
            async (file: File) => {
                const uploadResult = await upload(file)
                if (!uploadResult) {
                    return null
                }

                const attachment = {
                    resource: uploadResult.fileId,
                    url: uploadResult.url,
                    fileName: uploadResult.fileName,
                    contentType: uploadResult.file.type || undefined,
                    fileSizeBytes: uploadResult.file.size || undefined,
                }

                setImageAttachments((currentAttachments) => [...currentAttachments, attachment])

                return {
                    id: uploadResult.fileId,
                    url: uploadResult.url,
                    alt: uploadResult.fileName,
                }
            },
            [upload]
        )

        const handleSubmit = async () => {
            if (!message.trim() || isSubmitting || isUploading) {
                return
            }

            setIsSubmitting(true)

            try {
                await onSubmit({
                    message: message.trim(),
                    attachments: createAttachmentInput({
                        markdown: message,
                        imageAttachments,
                        fileAttachments,
                    }),
                })
                resetDraft()
            } finally {
                setIsSubmitting(false)
            }
        }

        return (
            <Card className="gap-0 p-0">
                <CardHeader className="gap-3 p-5">
                    <div className="flex items-start gap-4">
                        <Actor size="lg" actor={actor} />
                        <div className="min-w-0 flex-1 space-y-3">
                            <div className="min-w-0">
                                <div className="truncate font-medium">
                                    <ActorName actor={actor} />
                                </div>
                                {!!authorDescription && (
                                    <div className="text-muted-foreground text-sm">{authorDescription}</div>
                                )}
                            </div>
                            <Field>
                                <FieldLabel required>{messageLabel}</FieldLabel>
                                <RichTextEditor
                                    key={editorSessionKey}
                                    className="min-h-40"
                                    editorClassName="min-h-40"
                                    value={message}
                                    onChange={setMessage}
                                    maxLength={10000}
                                    minRows={0}
                                    maxRows={0}
                                    syntaxHighlighting
                                    features={{ headings: true, quote: true, codeBlock: true, taskList: true }}
                                    imageUpload={handleImageUpload}
                                    resources={createRichTextEditorResources(imageAttachments)}
                                />
                            </Field>
                            <Field>
                                <FieldLabel>{t('product.community.shared.attachments.title')}</FieldLabel>
                                <AttachmentEditor
                                    attachments={fileAttachments}
                                    disabled={isSubmitting || isUploading}
                                    upload={upload}
                                    onChange={setFileAttachments}
                                />
                            </Field>
                            <div className="flex justify-end gap-2">
                                {onCancel && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={isSubmitting || isUploading}
                                        onClick={onCancel}
                                    >
                                        <XIcon data-icon="inline-start" />
                                        {cancelLabel || t('common.cancel')}
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    loading={isSubmitting || isUploading}
                                    disabled={!message.trim()}
                                    onClick={() => void handleSubmit()}
                                >
                                    <CheckIcon data-icon="inline-start" />
                                    {submitLabel}
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardHeader>
            </Card>
        )
    }
)
